<script setup>
import { computed, ref } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  showSenderLabel,
  senderLabel,
} = useMessageContext();
const { t } = useI18n();

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  if (inReplyTo.value?.id) {
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
      messageId: inReplyTo.value.id,
    });
  }
};

const handleReplyToClick = event => {
  // Only scroll if the click is not on a download button
  if (!event.target.closest('button')) {
    // Prevent any default behavior (like opening images)
    event.preventDefault();
    event.stopPropagation();
    scrollToMessage();
  }
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyToPreview = computed(() => {
  if (!inReplyTo.value) return '';
  const { content, attachments } = inReplyTo.value;
  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;
    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return '';
});

const getAttachmentLabel = attachment => {
  const fileType = attachment.fileType ?? attachment.file_type;
  if (!fileType) return '';

  const key = `CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`;
  const translated = t(key);
  return translated === key ? fileType : translated;
};

const getAttachmentIcon = attachment => {
  const fileType = attachment.fileType ?? attachment.file_type;
  const iconMap = {
    image: 'image',
    video: '/assets/images/dashboard/channels/video.png',
    audio: 'mic',
    file: '/assets/images/dashboard/channels/docs.png',
    location: 'location-pin',
    contact: 'person',
    pdf: '/assets/images/dashboard/channels/docs.png',
    doc: '/assets/images/dashboard/channels/docs.png',
    docx: '/assets/images/dashboard/channels/docs.png',
    xls: '/assets/images/dashboard/channels/docs.png',
    xlsx: '/assets/images/dashboard/channels/docs.png',
    ppt: '/assets/images/dashboard/channels/docs.png',
    pptx: '/assets/images/dashboard/channels/docs.png',
    txt: '/assets/images/dashboard/channels/docs.png',
    zip: 'archive',
    rar: 'archive',
    mp3: 'music-note',
    mp4: '/assets/images/dashboard/channels/video.png',
    avi: '/assets/images/dashboard/channels/video.png',
    mov: '/assets/images/dashboard/channels/video.png',
    wav: 'mic',
    gif: 'image',
    jpg: 'image',
    jpeg: 'image',
    png: 'image',
    webp: 'image',
  };

  if (iconMap[fileType]) {
    return iconMap[fileType];
  }

  const fileName = attachment.fileName ?? attachment.file_name ?? '';
  const extension = fileName.split('.').pop()?.toLowerCase();

  if (extension && iconMap[extension]) {
    return iconMap[extension];
  }

  if (fileType === 'image' || fileType?.includes('image')) return 'image';
  if (fileType === 'video' || fileType?.includes('video')) {
    return '/assets/images/dashboard/channels/video.png';
  }
  if (fileType === 'audio' || fileType?.includes('audio')) return 'mic';
  if (fileType === 'document' || fileType?.includes('document')) {
    return '/assets/images/dashboard/channels/docs.png';
  }

  return '/assets/images/dashboard/channels/docs.png';
};

const isCustomIcon = icon => {
  return icon.startsWith('/assets/');
};

const imageLoadError = ref(false);

const getReplyContentPreview = computed(() => {
  if (!inReplyTo.value) return '';
  if (inReplyTo.value.content) return inReplyTo.value.content;
  return replyToPreview.value;
});

const getReplyAttachment = computed(() => {
  if (!inReplyTo.value?.attachments?.length) return null;
  return inReplyTo.value.attachments[0];
});

const isReplyImage = computed(() => {
  const attachment = getReplyAttachment.value;
  if (!attachment) return false;
  return attachment.fileType === 'image' || attachment.file_type === 'image';
});

const getReplyFileName = computed(() => {
  const attachment = getReplyAttachment.value;
  if (!attachment) return '';
  return attachment.fileName || attachment.file_name || '';
});

const getReplyFileType = computed(() => {
  const attachment = getReplyAttachment.value;
  if (!attachment) return '';
  return attachment.fileType || attachment.file_type || '';
});

const getReplyImageDataUrl = computed(() => {
  const attachment = getReplyAttachment.value;
  if (!attachment) return '';
  return attachment.dataUrl || attachment.data_url || '';
});

const getReplyAttachmentIcon = computed(() => {
  const attachment = getReplyAttachment.value;
  if (!attachment) return '';
  return getAttachmentIcon(attachment);
});

const getReplyAttachmentAlt = computed(() => {
  const attachment = getReplyAttachment.value;
  if (!attachment) return '';
  return attachment.fileType || attachment.file_type || '';
});

const getReplyAttachmentLabel = computed(() => {
  const attachment = getReplyAttachment.value;
  if (!attachment) return '';
  return getAttachmentLabel(attachment);
});

const getReplyAttachmentTypeLabel = computed(() => {
  const type = getReplyFileType.value;
  return type || getReplyAttachmentLabel.value;
});

const hasReplyAttachment = computed(() => Boolean(getReplyAttachment.value));
const hasReplyContent = computed(() => Boolean(getReplyContentPreview.value));

const canShowReplyImage = computed(() => {
  return Boolean(
    isReplyImage.value && getReplyImageDataUrl.value && !imageLoadError.value
  );
});

const isReplyAttachmentIconCustom = computed(() => {
  return Boolean(
    getReplyAttachmentIcon.value && isCustomIcon(getReplyAttachmentIcon.value)
  );
});

const showReplyContentWithImage = computed(() => {
  return Boolean(hasReplyContent.value && isReplyImage.value);
});

const showReplyContentWithAttachment = computed(() => {
  return Boolean(hasReplyContent.value && !isReplyImage.value);
});

const replyToImageStyle = computed(() => {
  if (!hasReplyAttachment.value || !isReplyImage.value) return {};

  const imageUrl = getReplyImageDataUrl.value;

  if (!imageUrl) return {};

  return {
    backgroundImage: `url(${imageUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <p
      v-if="showSenderLabel"
      class="text-xs font-medium mb-1 opacity-70 truncate"
    >
      {{ senderLabel }}
    </p>
    <div
      v-if="inReplyTo"
      class="bg-n-alpha-black1 rounded-lg p-2 -mx-1 mb-2 cursor-pointer"
      @click="handleReplyToClick"
    >
      <template v-if="hasReplyAttachment && isReplyImage">
        <div class="flex items-center gap-3">
          <div class="relative group">
            <div
              v-if="canShowReplyImage"
              :style="replyToImageStyle"
              class="w-24 h-24 rounded-lg border border-n-slate-6 flex-shrink-0 cursor-pointer select-none"
              @click.stop="scrollToMessage"
            />
            <div
              v-else
              class="w-24 h-24 rounded-lg border border-n-slate-6 flex-shrink-0 flex items-center justify-center bg-n-slate-6 cursor-pointer"
              @click.stop="scrollToMessage"
            >
              <img
                v-if="isReplyAttachmentIconCustom"
                :src="getReplyAttachmentIcon"
                :alt="getReplyAttachmentAlt"
                class="w-12 h-12 object-contain"
              />
              <fluent-icon
                v-else
                :icon="getReplyAttachmentIcon"
                size="24"
                class="text-n-slate-11"
              />
            </div>
          </div>
          <span
            v-if="showReplyContentWithImage"
            class="line-clamp-2 break-all flex-1"
          >
            {{ getReplyContentPreview }}
          </span>
        </div>
      </template>
      <template v-else-if="hasReplyAttachment">
        <div class="flex items-center gap-3">
          <div class="relative group">
            <div
              class="w-12 h-12 flex items-center justify-center bg-n-slate-6 rounded-lg flex-shrink-0"
            >
              <img
                v-if="isReplyAttachmentIconCustom"
                :src="getReplyAttachmentIcon"
                :alt="getReplyAttachmentAlt"
                class="w-8 h-8 object-contain"
              />
              <fluent-icon
                v-else
                :icon="getReplyAttachmentIcon"
                size="24"
                class="text-n-slate-11"
              />
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div
              v-if="getReplyFileName"
              class="text-xs font-medium text-n-slate-11 truncate"
            >
              {{ getReplyFileName }}
            </div>
            <div
              v-if="getReplyAttachmentTypeLabel"
              class="text-xs text-n-slate-10 capitalize"
            >
              {{ getReplyAttachmentTypeLabel }}
            </div>
            <span
              v-if="showReplyContentWithAttachment"
              class="line-clamp-2 break-all text-xs mt-1"
            >
              {{ getReplyContentPreview }}
            </span>
          </div>
        </div>
      </template>
      <template v-else-if="inReplyTo._loading">
        <span class="line-clamp-2 break-all text-n-slate-11 italic">
          {{ t('CONVERSATION.REPLY_MESSAGE_LOADING') }}
        </span>
      </template>
      <template v-else>
        <span class="line-clamp-2 break-all">
          {{
            getReplyContentPreview || t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND')
          }}
        </span>
      </template>
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
      ]"
      class="mt-2"
    />
  </div>
</template>

<style scoped>
/* Prevent default image behaviors */
img {
  pointer-events: auto;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  -user-drag: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>
